import { Link } from 'react-router-dom';
import { ChevronDown, Phone, Play, Plane, Briefcase, Heart, Users, Wifi, Shield, Luggage } from 'lucide-react';
import BannerImage from "../assets/images/banner-suburban.webp";
import airportImage from '../assets/images/airport-transfers.webp';
import corporateImage from '../assets/images/corporate-transportation.webp';
import specialOccasionImage from '../assets/images/special-occasions.webp';
import LuxuryImage from "../assets/images/luxury-suburban.webp";

export default function Home() {
  return (
    <div className="font-inter">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${BannerImage})`
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white animate-fade-in-up">
          <h1 className="text-5xl md:text-7xl font-playfair font-bold mb-6 leading-tight drop-shadow-md">
            Pinnacle <span className="text-luxury-gold">Premier</span><br />
            Limo Service
          </h1>
          <p className="text-xl md:text-2xl mb-8 font-light max-w-3xl mx-auto">
          We provide luxury transportation for airport transfers, corporate events, weddings, proms, and other special occasions. Our fleet is maintained to the highest standards, and our professional chauffeurs ensure every ride is smooth, stylish, and on time.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="bg-luxury-gold  rounded text-deep-black hover:bg-yellow-600 font-semibold px-8 py-4 text-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center animate-pulse">
                <Phone className="mr-2 h-5 w-5" />
                Book Your Ride
              </button>
            </Link>
            <Link to="/services">
              <button className="border-2 rounded border-white text-white hover:bg-white hover:text-deep-black font-semibold px-8 py-4 text-lg transition-all duration-300 flex items-center justify-center">
                <Play className="mr-2 h-5 w-5" />
                View Our Fleet
              </button>
            </Link>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-smooth-bounce">
          <ChevronDown className="text-white h-8 w-8" />
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 ">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-playfair font-bold  mb-6 relative inline-block">
              Our Premium <span>Services</span>
           
            </h2>
            <p className="text-xl  max-w-3xl mx-auto">
              We provide exceptional limousine services tailored to meet your specific needs with our fleet of luxury vehicles.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              { icon: Plane, title: "Airport Transfers", desc: "Reliable and punctual airport transportation service. Professional chauffeurs ensure you arrive on time, every time.", image: airportImage },
              { icon: Briefcase, title: "Corporate Events", desc: "Professional transportation for business meetings, conferences, and corporate events with discretion and comfort.", image: corporateImage },
              { icon: Heart, title: "Special Occasions", desc: "Make your special moments unforgettable with our luxury transportation for weddings, anniversaries, and celebrations.", image: specialOccasionImage },
            ].map((service, idx) => (
              <div 
                key={idx} 
                className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                <div 
                  className="h-48 bg-cover bg-center"
                  style={{ backgroundImage: `url(${service.image})` }}
                />
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <service.icon className="text-luxury-gold h-6 w-6 mr-3" />
                    <h3 className="text-xl font-playfair font-semibold text-deep-black">{service.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-4">{service.desc}</p>
                  <div className="flex justify-center">
                    <Link to="/contact">
                      <button className="bg-luxury-gold rounded text-deep-black hover:bg-yellow-600 font-semibold px-6 py-2 text-lg transform hover:scale-105 transition-all duration-300">
                        Book Now
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Fleet Showcase */}
          <div className="bg-gradient-to-r from-gray-900 to-black p-8 rounded-xl shadow-xl">
            <h3 className="text-3xl font-playfair font-bold text-center text-white mb-8">
              Our Luxury <span className="text-luxury-gold">Suburban Fleet</span>
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="relative group">
                <img 
                  src={LuxuryImage}
                  alt="Luxury Chevrolet Suburban" 
                  className="rounded-xl shadow-lg w-full h-auto group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-xl"></div>
              </div>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <Users className="text-luxury-gold h-6 w-6 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-lg text-white">Spacious Seating</h4>
                    <p className="text-gray-300">Comfortably accommodates up to 8 passengers with premium leather seating</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Wifi className="text-luxury-gold h-6 w-6 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-lg text-white">Modern Amenities</h4>
                    <p className="text-gray-300">Complimentary Wi-Fi, climate control, and premium entertainment system</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Shield className="text-luxury-gold h-6 w-6 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-lg text-white">Safety First</h4>
                    <p className="text-gray-300">Latest safety features and professionally trained, licensed chauffeurs</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Luggage className="text-luxury-gold h-6 w-6 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-lg text-white">Ample Storage</h4>
                    <p className="text-gray-300">Generous cargo space for luggage, equipment, and personal belongings</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Pinnacle premier */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-playfair font-bold mb-6 relative inline-block">
              Why Choose <span className="text-luxury-gold">Pinnacle premier</span>
              <span className="absolute bottom-0 left-0 w-full h-1 bg-luxury-gold"></span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Experience the difference of true luxury transportation with our commitment to excellence, reliability, and unmatched service quality.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {[
              { icon: Shield, title: "Licensed & Insured", desc: "Fully licensed chauffeurs and comprehensive insurance coverage for complete peace of mind during your journey." },
              { icon: Phone, title: "24/7 Availability", desc: "Round-the-clock service availability for all your transportation needs, including emergency and last-minute bookings." },
              { icon: Heart, title: "Personalized Service", desc: "Tailored transportation solutions that cater to your specific needs and preferences for every occasion." },
            ].map((item, idx) => (
              <div 
                key={idx} 
                className="text-center p-6 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                <div className="bg-luxury-gold text-deep-black w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <item.icon className="h-10 w-10" />
                </div>
                <h3 className="text-xl font-playfair font-semibold mb-4 text-luxury-gold">{item.title}</h3>
                <p className="text-gray-300">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-gray-800 p-8 rounded-xl">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
              {[
                { value: "10+", label: "Years of Excellence" },
                { value: "500+", label: "Satisfied Clients" },
                { value: "24/7", label: "Service Available" },
                { value: "100%", label: "Safety Record" },
              ].map((stat, idx) => (
                <div key={idx} className="hover:scale-105 transition-transform duration-300">
                  <div className="text-3xl font-bold text-luxury-gold mb-2">{stat.value}</div>
                  <div className="text-sm text-gray-300">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Booking Process */}
      <section 
        className="py-20 bg-gray-100" 
        style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/leather.png")', backgroundSize: '200px' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-deep-black mb-6">
              Simple <span className="text-luxury-gold">Booking Process</span>
            </h2>
            <p className="text-xl text-charcoal max-w-3xl mx-auto">
              Booking your luxury Suburban transportation is quick and easy with our streamlined three-step process.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              { step: "1", title: "Request Quote", desc: "Contact us with your transportation needs including date, time, pickup location, and destination.", note: "Average response time: 15 minutes" },
              { step: "2", title: "Confirm Details", desc: "Review your customized quote and confirm all service details, special requirements, and payment method.", note: "Transparent pricing, no hidden fees" },
              { step: "3", title: "Enjoy Your Ride", desc: "Relax in luxury as our professional chauffeur provides exceptional service in our premium Suburban.", note: "Real-time tracking available" },
            ].map((item, idx) => (
              <div 
                key={idx} 
                className="text-center bg-white p-6 rounded-lg shadow-lg hover:-translate-y-2 transition-all duration-300"
              >
                <div className="bg-luxury-gold text-deep-black w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="text-xl font-playfair font-semibold mb-4 text-deep-black">{item.title}</h3>
                <p className="text-gray-600 mb-4">{item.desc}</p>
                <div className="bg-gray-100 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">{item.note}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/contact">
              <button className="bg-luxury-gold rounded text-deep-black hover:bg-yellow-600 font-semibold px-8 py-4 text-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center mx-auto animate-pulse">
                <Phone className="mr-2 h-5 w-5" />
                Start Your Booking
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-white mb-6 relative inline-block">
              Service <span className="text-luxury-gold">Areas</span>
              <span className="absolute bottom-0 left-0 w-full h-1 bg-luxury-gold"></span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We proudly serve the metropolitan area and surrounding regions with reliable, luxury transportation services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Plane, title: "Airport Transfers", desc: "All major airports within 50-mile radius" },
              { icon: Briefcase, title: "Downtown Area", desc: "Business district & corporate centers" },
              { icon: Heart, title: "Event Venues", desc: "Hotels, wedding venues & special locations" },
              { icon: Users, title: "Suburban Areas", desc: "Residential communities & suburbs" },
            ].map((item, idx) => (
              <div
                key={idx}
                className="text-center p-6 rounded-lg bg-white text-deep-black shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                <div className="bg-luxury-gold text-deep-black w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-6 w-6" />
                </div>
                <h3 className="font-semibold mb-2 text-deep-black">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="bg-gray-100 p-8 rounded-lg text-deep-black shadow-lg">
              <h3 className="text-2xl font-playfair font-semibold mb-4">
                Don't See Your Location?
              </h3>
              <p className="text-gray-700 mb-6">
                We regularly extend our service area based on client needs. Contact us to discuss transportation to your specific destination.
              </p>
              <Link to="/contact">
                <button className="border-2 border-luxury-gold text-luxury-gold hover:bg-luxury-gold hover:text-deep-black font-semibold px-6 py-3">
                  Ask About Your Area
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-playfair font-bold text-deep-black mb-6">
              Client <span className="text-luxury-gold">Testimonials</span>
            </h2>
            <p className="text-xl text-charcoal max-w-3xl mx-auto">
              Hear what our valued clients say about their experience with Pinnacle premier Limo.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { name: "Sarah Johnson", title: "Corporate Executive", quote: "Exceptional service from start to finish. The Suburban was immaculate and our chauffeur was professional and punctual. Perfect for our corporate event.", rotation: "-rotate-2" },
              { name: "Michael & Lisa Chen", title: "Wedding Clients", quote: "Made our wedding day extra special. The luxury Suburban was perfect for our bridal party. Highly recommend Pinnacle premier for special occasions.", rotation: "rotate-2" },
              { name: "Robert Martinez", title: "Frequent Traveler", quote: "Reliable airport transfers every time. Never missed a flight thanks to their punctual service and flight tracking. The best in the business.", rotation: "-rotate-3" },
            ].map((item, idx) => (
              <div 
                key={idx} 
                className={`bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 ${item.rotation} animate-fade-in-up`}
              >
                <div className="flex items-center mb-4">
                  <div className="flex text-luxury-gold">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                      </svg>
                    ))}
                  </div>
                </div>
                <p className="text-gray-600 mb-4 italic">{item.quote}</p>
                <div className="font-semibold text-deep-black">{item.name}</div>
                <div className="text-sm text-gray-500">{item.title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-b from-luxury-gold to-yellow-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-white mb-6 drop-shadow-md">
            Ready to Experience <span className="text-deep-black">Luxury?</span>
          </h2>
          <p className="text-xl text-white max-w-3xl mx-auto mb-8">
            Book your premium Suburban transportation today and discover why Pinnacle premier Limo 
            is the preferred choice for discerning clients who demand excellence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="bg-deep-black text-luxury-gold hover:bg-gray-800 font-semibold px-8 py-4 text-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center animate-pulse">
                <Phone className="mr-2 h-5 w-5" />
                Book Your Limo Now
              </button>
            </Link>
            <button 
              className="border-2 border-white text-white hover:bg-white hover:text-deep-black font-semibold px-8 py-4 text-lg transition-all duration-300 flex items-center justify-center"
              onClick={() => window.location.href = 'tel:+12814286291'}
            >
              <Phone className="mr-2 h-5 w-5" />
              Call (281) 428-6291
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}